import {
	loadStripe,
	Stripe,
	StripeElements,
	PaymentIntentResult,
	PaymentIntentConfirmParams,
	ConfirmKlarnaPaymentData,
} from '@stripe/stripe-js';
import { CreatePaymentIntentDto } from '@sweatpals/shared';
import { STRIPE_PUBLISHABLE_KEY } from '../constants';
import { CheckoutFormDto } from '../interfaces/checkout-form.dto';
import { HOME_PAGE_URL } from '../constants';

export class StripeService {
	public stripe: Stripe | null;

	constructor() {
		this.loadStripe();
	}

	loadStripe = async () => {
		this.stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);
	};

	public async confirmCardPayment(
		elements: StripeElements,
		clientSecret?: string,
		confirmParams?: Partial<PaymentIntentConfirmParams>
	): Promise<PaymentIntentResult> {
		if (clientSecret) {
			const { error } = await elements.submit();

			if (error) {
				return { error };
			}
		}

		if (!this.stripe) {
			throw new Error('Stripe.js not loaded');
		}

		return await this.stripe.confirmPayment({
			elements,
			clientSecret,
			redirect: 'if_required',
			confirmParams,
		});
	}

	public async confirmKlarnaPayment(
		clientSecret: string,
		data: ConfirmKlarnaPaymentData
	): Promise<PaymentIntentResult> {
		if (!this.stripe) {
			throw new Error('Stripe.js not loaded');
		}

		console.debug('confirmKlarnaPayment', data);

		return await this.stripe.confirmKlarnaPayment(clientSecret, data);
	}

	public async handleConfirmPayment(
		paymentType: string,
		elements: StripeElements,
		paymentIntent: CreatePaymentIntentDto,
		data: CheckoutFormDto,
	): Promise<PaymentIntentResult> {
		const clientSecret = paymentIntent.intent.paymentIntent;
		const returnUrl = paymentIntent.orderId ? `${HOME_PAGE_URL}/order/${paymentIntent.orderId}` : undefined;

		switch (paymentType) {
			case 'klarna': {
				const addressElement = elements.getElement('address');
				const addressValue = addressElement ? (await addressElement.getValue())?.value : null;
				const confirmParams: ConfirmKlarnaPaymentData = {
					return_url: returnUrl,
					payment_method: {
						billing_details: {
							name: data.fullName,
							email: data.email,
							phone: data.phone,
							address: addressValue.address,
						},
					},
				};
				return await this.confirmKlarnaPayment(clientSecret, confirmParams);
			}
			default: {
				const confirmParams: Partial<PaymentIntentConfirmParams> = {
					return_url: returnUrl,
				};
				return await this.confirmCardPayment(elements, clientSecret, confirmParams);
			}
		}
	}
}
